import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getRiskIndicatorByRiskRule } from '@silverfort-nodelibs/common/utils/insight';
import useGetInsightEntities, { InsightEntitiesFilters } from '../api/queries/useGetInsightEntities';
import { EmptyStateIcons, SearchButton, showAlert, Table } from '@silverfort/sf-components';
import Filters from './Filters/Filters';
import ExportButton from './ExportButton';
import { InsightEntity } from '../api/types/Insights';
import useGetColumns from './useGetColumns';
import useGetFilters from './useGetFilters';
import { INSIGHT_STATUS_DESCRIPTIONS, ROLES } from '@silverfort-nodelibs/common/utils/consts';
import UserSessionContext from '../../../components/PageWrapper/UserSessionContext';
import { isUserPermitted } from '../../../components/utils';
import useGetRefSize from '../../../hooks/useGetRefSize';

const PAGE_SIZE = 100;
const TABLE_HEADER_HEIGHT = 56;

const getRows = (
    insightsDetailsAdded: string[] = [],
    insightsDetails: InsightEntity[] = [],
    hiddenRiskIndicators: string[] = []
) => {
    const newGuidsSet = new Set(insightsDetailsAdded);
    const insights = insightsDetails.map((insight) => {
        return {
            ...insight,
            isNew: newGuidsSet.has(insight.guid) || insight.isNew,
            risk_indicators: (insight.risk_indicators ?? []).filter(
                (riskIndicator) => !hiddenRiskIndicators.includes(riskIndicator)
            )
        };
    });
    return [...insights];
};

export interface SnackbarDataProps {
    selectedEntities: string[];
    allEntities: InsightEntity[];
    onClose: () => void;
    onSuccess: (s: string) => void;
}

interface Props {
    insightRuleName: string;
    diffInDaysFilter?: number;
    sourceTypeFilter?: string;
    domainNameFilter?: string[];
    isInventoryTable?: boolean;
    setSnackbarData?: (data: SnackbarDataProps) => void;
    hiddenRiskIndicators?: string[];
    onEntityStatusUpdate?: (newStatusDescription: string) => void;
}

const EntitiesTable: FC<Props> = ({
    insightRuleName,
    diffInDaysFilter,
    sourceTypeFilter,
    domainNameFilter,
    isInventoryTable,
    setSnackbarData,
    hiddenRiskIndicators = [],
    onEntityStatusUpdate
}) => {
    const [sortColumn, setSortColumn] = useState<string>();
    const [sortDirection, setSortDirection] = useState<string>();
    const [riskLevelFilter, setRiskLevelFilter] = useState<number[]>();
    const [entityTypeFilter, setEntityTypeFilter] = useState<number[]>();
    const [riskIndicatorFilter, setRiskIndicatorFilter] = useState<string[]>();
    const [searchedEntity, setSearchedEntity] = useState<string>('');
    const [selectedEntities, setSelectedEntities] = useState<string[]>([]);
    const [idpFilter, setIdpFilter] = useState<string[]>();
    const [entityStatus, setEntityStatus] = useState<string[] | undefined>([
        INSIGHT_STATUS_DESCRIPTIONS.OPEN,
        INSIGHT_STATUS_DESCRIPTIONS.IN_PROGRESS
    ]);

    const { userData } = useContext(UserSessionContext);
    const insightsTitle = getRiskIndicatorByRiskRule(insightRuleName)?.insightsTitle;
    const isSelectable = useMemo(
        () => !isInventoryTable && isUserPermitted(userData?.user, 'insights', ROLES.EDITOR.value),
        [userData?.user, isInventoryTable]
    );
    const filters = useGetFilters({
        riskLevelFilter,
        setRiskLevelFilter,
        entityTypeFilter,
        setEntityTypeFilter,
        riskIndicatorFilter,
        setRiskIndicatorFilter,
        entityStatus,
        setEntityStatus,
        idpFilter,
        setIdpFilter,
        hiddenRiskIndicators,
        isWithStatusFilter: !isInventoryTable
    });

    const entityStatusFilter = useMemo(() => {
        return isInventoryTable ? [] : entityStatus;
    }, [entityStatus, isInventoryTable]);

    const requestFilters: InsightEntitiesFilters = useMemo(() => {
        return {
            ruleName: insightRuleName,
            daysDiff: diffInDaysFilter,
            entitySourceType: sourceTypeFilter,
            domainName: domainNameFilter,
            entityType: entityTypeFilter,
            riskLevel: riskLevelFilter,
            riskIndicator: riskIndicatorFilter,
            entityName: searchedEntity,
            platforms: idpFilter,
            entityStatus: entityStatusFilter,
            pageSize: PAGE_SIZE,
            sortField: sortColumn,
            sortDirection,
            activeOnly: isInventoryTable
        };
    }, [
        insightRuleName,
        diffInDaysFilter,
        sourceTypeFilter,
        domainNameFilter,
        entityTypeFilter,
        riskLevelFilter,
        riskIndicatorFilter,
        searchedEntity,
        idpFilter,
        entityStatusFilter,
        sortColumn,
        sortDirection,
        isInventoryTable
    ]);

    const { mergedPages, isFetching, hasNextPage, fetchNextPage, refreshTable } = useGetInsightEntities(requestFilters);

    const rows = useMemo(() => {
        return getRows(mergedPages.insightsDetailsAdded, mergedPages.insightsDetails, hiddenRiskIndicators);
    }, [mergedPages.insightsDetails, mergedPages.insightsDetailsAdded, hiddenRiskIndicators]);

    const onUpdateEntityStatusError = useCallback(
        (newStatusDescription: string) => {
            showAlert(`Failed to update status to ${newStatusDescription}`, 'error');
        },
        []
    );

    const onUpdateEntityStatusSuccess = useCallback(
        (newStatusDescription: string) => {
            showAlert(`Successfully updated status to ${newStatusDescription}`, 'success');
            refreshTable();
            onEntityStatusUpdate?.(newStatusDescription);
        },
        [refreshTable, onEntityStatusUpdate]
    );

    const columns = useGetColumns(insightRuleName, rows, isInventoryTable, onUpdateEntityStatusSuccess, onUpdateEntityStatusError);

    const handleSort = (columnKey: string, sortDirection: string) => {
        setSortColumn(columnKey);
        setSortDirection(sortDirection);
    };

    const onSnackBarClose = () => {
        setSelectedEntities([]);
    };

    const isOneOfTheFiltersActive = () => {
        return (
            filters.filter((filter) =>
                Array.isArray(filter.selectedItems) ? filter.selectedItems?.length : !!filter.selectedItems
            ).length || searchedEntity.length
        );
    };

    const handleSelect = (selectedRows: string[]) => {
        setSelectedEntities(selectedRows);
    };

    useEffect(() => {
        const snackbarData = {
            onClose: onSnackBarClose,
            selectedEntities: selectedEntities,
            allEntities: rows,
            onSuccess: onUpdateEntityStatusSuccess
        };
        setSnackbarData?.(snackbarData);
    }, [selectedEntities, rows]);
    
    const ref = useRef(null);
    const { height } = useGetRefSize(ref);

    return (
        <>
            <div className="flex flex-col flex-1 gap-4 w-full h-full" ref={ref}>
                <div className="flex flex-none justify-between w-full h-fit">
                    <Filters allFilterOptions={filters} />
                    <div className="w-[300px] h-full flex justify-end gap-2 p-1">
                        <SearchButton setSearchString={setSearchedEntity} searchString={searchedEntity} iconOnly />
                        <ExportButton
                            insightRuleName={insightRuleName}
                            insightTitle={insightsTitle}
                            filters={requestFilters}
                        />
                    </div>
                </div>
                <div>
                    {/* @ts-ignore */}
                    <Table
                        loading={isFetching}
                        columns={columns}
                        onScrollEnd={() => {
                            if (hasNextPage) {
                                fetchNextPage();
                            }
                        }}
                        bodyHeight={(height ?? 0) - TABLE_HEADER_HEIGHT}
                        rows={rows}
                        EmptyStateProps={{
                            className: 'h-max py-8',
                            title: !isOneOfTheFiltersActive() ? 'Nothing to show here' : 'No Results',
                            description: !isOneOfTheFiltersActive()
                                ? `There are no exposures from ${insightsTitle} in your network`
                                : 'There are no results matching to your search',
                            icon: isOneOfTheFiltersActive() ? EmptyStateIcons.NoResults : undefined
                        }}
                        selectable={isSelectable}
                        onSortChange={handleSort}
                        keyExtractor={(row) => row?.guid}
                        onSelect={handleSelect}
                        selected={selectedEntities}
                    />
                </div>
            </div>
        </>
    );
};

export default EntitiesTable;
